<script setup>
import { reactive, computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { URLPattern } from 'urlpattern-polyfill';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  selectedCampaign: {
    type: Object,
    default: () => ({}),
  },
  showActionButtons: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();
const { isCloudFeatureEnabled } = useAccount();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  inboxes: useMapGetter('inboxes/getWebsiteInboxes'),
  knowledgebases: useMapGetter('knowledgebase/getLibraries'),
};

const isPersonalizedCampaignsEnabled = computed(() => {
  return isCloudFeatureEnabled(FEATURE_FLAGS.PERSONALIZED_CAMPAIGNS);
});

const senderList = ref([]);

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  senderId: 0,
  enabled: true,
  triggerOnlyDuringBusinessHours: false,
  endPoint: '',
  timeOnPage: 10,
  personalizationEnabled: false,
  personalizationDescription: '',
  knowledgebaseId: null,
};

const state = reactive({ ...initialState });

const urlValidators = {
  shouldBeAValidURLPattern: value => {
    try {
      // eslint-disable-next-line
      new URLPattern(value);
      return true;
    } catch {
      return false;
    }
  },
  shouldStartWithHTTP: value =>
    value ? value.startsWith('https://') || value.startsWith('http://') : false,
};

const validationRules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  senderId: { required },
  endPoint: { required, ...urlValidators },
  timeOnPage: { required },
  personalizationDescription: {},
};

const v$ = useVuelidate(validationRules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const knowledgebaseOptions = computed(() => {
  const catalogueKnowledgebases =
    formState.knowledgebases.value?.filter(kb => kb.type === 'catalog') || [];

  return [
    { value: null, label: 'None (No catalogue integration)' },
    ...mapToOptions(catalogueKnowledgebases, 'id', 'name'),
  ];
});

const sendersAndBotList = computed(() => [
  { value: 0, label: 'Bot' },
  ...mapToOptions(senderList.value, 'id', 'name'),
]);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.LIVE_CHAT.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  endPoint: getErrorMessage('endPoint', 'END_POINT'),
  timeOnPage: getErrorMessage('timeOnPage', 'TIME_ON_PAGE'),
  sender: getErrorMessage('senderId', 'SENT_BY'),
  personalizationDescription: getErrorMessage(
    'personalizationDescription',
    'PERSONALIZATION_DESCRIPTION'
  ),
}));

const resetState = () => Object.assign(state, initialState);

const handleCancel = () => emit('cancel');

const handleInboxChange = async inboxId => {
  if (!inboxId) {
    senderList.value = [];
    return;
  }

  try {
    const response = await store.dispatch('inboxMembers/get', { inboxId });
    senderList.value = response?.data?.payload ?? [];
  } catch (error) {
    senderList.value = [];
    useAlert(
      error?.response?.message ??
        t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.API.ERROR_MESSAGE')
    );
  }
};

const prepareCampaignDetails = () => {
  const campaignDetails = {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    sender_id: state.senderId || null,
    enabled: state.enabled,
    trigger_only_during_business_hours: state.triggerOnlyDuringBusinessHours,
    trigger_rules: {
      url: state.endPoint,
      time_on_page: state.timeOnPage,
    },
  };

  // Only include personalization details if the feature is enabled
  if (isPersonalizedCampaignsEnabled.value) {
    campaignDetails.personalization_enabled = state.personalizationEnabled;
    campaignDetails.personalization_description =
      state.personalizationDescription;
    campaignDetails.knowledgebase_id = state.knowledgebaseId;
  }

  return campaignDetails;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  if (props.mode === 'create') {
    resetState();
    handleCancel();
  }
};

const updateStateFromCampaign = campaign => {
  if (!campaign) return;

  const {
    title,
    message,
    inbox: { id: inboxId },
    sender,
    enabled,
    trigger_only_during_business_hours: triggerOnlyDuringBusinessHours,
    personalization_enabled: personalizationEnabled = false,
    personalization_description: personalizationDescription = '',
    knowledgebase_id: knowledgebaseId = null,
    trigger_rules: { url: endPoint, time_on_page: timeOnPage },
  } = campaign;

  Object.assign(state, {
    title,
    message,
    inboxId,
    senderId: sender?.id ?? 0,
    enabled,
    triggerOnlyDuringBusinessHours,
    endPoint,
    timeOnPage,
  });

  // Only set personalization fields if the feature is enabled
  if (isPersonalizedCampaignsEnabled.value) {
    Object.assign(state, {
      personalizationEnabled,
      personalizationDescription,
      knowledgebaseId,
    });
  } else {
    // Reset personalization fields when feature is disabled
    Object.assign(state, {
      personalizationEnabled: false,
      personalizationDescription: '',
      knowledgebaseId: null,
    });
  }
};

watch(
  () => state.inboxId,
  newInboxId => {
    if (newInboxId) {
      handleInboxChange(newInboxId);
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedCampaign,
  newCampaign => {
    if (props.mode === 'edit' && newCampaign) {
      updateStateFromCampaign(newCampaign);
    }
  },
  { immediate: true }
);

// Load knowledgebases on component mount
onMounted(async () => {
  try {
    await store.dispatch('knowledgebase/get');
  } catch (error) {
    // Silently handle knowledgebase loading error
  }
});

defineExpose({ prepareCampaignDetails, isSubmitDisabled });
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <Editor
      v-model="state.message"
      :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="sentBy" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.LABEL') }}
      </label>
      <ComboBox
        id="sentBy"
        v-model="state.senderId"
        :options="sendersAndBotList"
        :has-error="!!formErrors.sender"
        :disabled="!state.inboxId"
        :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        :message="formErrors.sender"
      />
    </div>

    <Input
      v-model="state.endPoint"
      type="url"
      :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.LABEL')"
      :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.PLACEHOLDER')"
      :message="formErrors.endPoint"
      :message-type="formErrors.endPoint ? 'error' : 'info'"
    />

    <Input
      v-model="state.timeOnPage"
      type="number"
      :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TIME_ON_PAGE.LABEL')"
      :placeholder="
        t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TIME_ON_PAGE.PLACEHOLDER')
      "
      :message="formErrors.timeOnPage"
      :message-type="formErrors.timeOnPage ? 'error' : 'info'"
    />

    <fieldset
      v-if="isPersonalizedCampaignsEnabled"
      class="flex flex-col gap-2.5"
    >
      <legend class="mb-2.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.PERSONALIZATION.TITLE') }}
      </legend>

      <label class="flex items-center gap-2">
        <input v-model="state.personalizationEnabled" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.PERSONALIZATION.ENABLED') }}
        </span>
      </label>

      <div v-if="state.personalizationEnabled" class="flex flex-col gap-1">
        <label
          for="personalizationDescription"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{
            t(
              'CAMPAIGN.LIVE_CHAT.CREATE.FORM.PERSONALIZATION.DESCRIPTION.LABEL'
            )
          }}
        </label>
        <textarea
          id="personalizationDescription"
          v-model="state.personalizationDescription"
          rows="3"
          :placeholder="
            t(
              'CAMPAIGN.LIVE_CHAT.CREATE.FORM.PERSONALIZATION.DESCRIPTION.PLACEHOLDER'
            )
          "
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak focus:outline-none focus:ring-2 focus:ring-n-blue-9"
          :class="{
            'border-red-500': formErrors.personalizationDescription,
          }"
        />
        <p
          v-if="formErrors.personalizationDescription"
          class="mt-1 text-xs text-red-500"
        >
          {{ formErrors.personalizationDescription }}
        </p>
        <p class="mt-1 text-xs text-n-slate-10">
          {{
            t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.PERSONALIZATION.DESCRIPTION.HELP')
          }}
        </p>
      </div>
    </fieldset>

    <fieldset
      v-if="isPersonalizedCampaignsEnabled && state.personalizationEnabled"
      class="flex flex-col gap-2.5"
    >
      <legend class="mb-2.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.CATALOGUE.TITLE') }}
      </legend>

      <div class="flex flex-col gap-1">
        <label
          for="knowledgebaseSelect"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.CATALOGUE.LABEL') }}
        </label>
        <ComboBox
          id="knowledgebaseSelect"
          v-model="state.knowledgebaseId"
          :options="knowledgebaseOptions"
          :placeholder="
            t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.CATALOGUE.PLACEHOLDER')
          "
          class="w-full"
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.CATALOGUE.HELP') }}
        </p>
      </div>
    </fieldset>

    <fieldset class="flex flex-col gap-2.5">
      <legend class="mb-2.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.OTHER_PREFERENCES.TITLE') }}
      </legend>

      <label class="flex items-center gap-2">
        <input v-model="state.enabled" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.OTHER_PREFERENCES.ENABLED') }}
        </span>
      </label>

      <label class="flex items-center gap-2">
        <input v-model="state.triggerOnlyDuringBusinessHours" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'CAMPAIGN.LIVE_CHAT.CREATE.FORM.OTHER_PREFERENCES.TRIGGER_ONLY_BUSINESS_HOURS'
            )
          }}
        </span>
      </label>
    </fieldset>

    <div
      v-if="showActionButtons"
      class="flex items-center justify-between w-full gap-3"
    >
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 n-woot-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="
          mode === 'create'
            ? t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.BUTTONS.CREATE')
            : t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.BUTTONS.EDIT')
        "
        class="w-full"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
